// store/slices/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import {
  getOtpToken,
  setOtpToken,
  setAuthData,
  clearAuthData,
  isAuthenticated,
  getRefreshToken,
  getUserData,
} from "../../utils/authUtils";
import {
  getUserProfile,
  updateProfile,
  updateBankDetails,
} from "./profileSlice";

// Send OTP (handles both send and resend)
export const sendOTP = createAsyncThunk(
  "auth/sendOTP",
  async (mobile, { rejectWithValue }) => {
    try {
      const otpToken = getOtpToken();
      const response = otpToken
        ? await api.resendOTP(mobile, otpToken)
        : await api.sendOTP(mobile);

      if (response.success === true) {
        if (response.data?.otp_token) {
          setOtpToken(response.data.otp_token);
        }
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to send OTP");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send OTP",
      );
    }
  },
);

// Device auth responses normally include user. Recover from /me when an
// older or partially loaded device response omits that relationship.
const resolveSignedInUser = async (data) => {
  const embedded = data?.user;
  if (typeof embedded?.name === "string" && embedded.name.trim()) {
    return embedded;
  }

  try {
    const profile = await api.getProfile();
    if (profile.success === true) {
      const user = profile.data?.user || profile.data;
      if (typeof user?.name === "string" && user.name.trim()) return user;
    }
  } catch {
    // A profile read failure must not invalidate an otherwise valid OTP login.
  }
  return embedded ?? null;
};

// Login with OTP
export const loginWithOTP = createAsyncThunk(
  "auth/loginWithOTP",
  async ({ mobile, otp, fcmToken }, { rejectWithValue }) => {
    try {
      const otpToken = getOtpToken();
      const response = await api.verifyOTP(
        mobile,
        otp,
        otpToken,
        fcmToken,
      );

      if (response.success === true) {
        setAuthData(response.data);
        const user = await resolveSignedInUser(response.data);
        if (user) localStorage.setItem("user_data", JSON.stringify(user));
        localStorage.removeItem("otp_token");
        return { ...response.data, user };
      }
      return rejectWithValue(response.message || "Login failed");
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
);

// Register User
export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (userData, { rejectWithValue }) => {
    try {
      const otpToken = getOtpToken();
      const response = await api.register({
        ...userData,
        otp_token: otpToken,
      });
      if (response.success === true) {
        setAuthData(response.data);
        const user = await resolveSignedInUser(response.data);
        if (user) localStorage.setItem("user_data", JSON.stringify(user));
        localStorage.removeItem("otp_token");
        return { ...response.data, user };
      }
      return rejectWithValue(response.message || "Registration failed");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Registration failed",
      );
    }
  },
);

// Get User Info
export const getUserInfo = createAsyncThunk(
  "auth/getUserInfo",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getProfile();
      if (response.success === true) {
        const user = response.data?.user || response.data;
        localStorage.setItem("user_data", JSON.stringify(user));
        return user;
      }
      return rejectWithValue(response.message || "Failed to get user info");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get user info",
      );
    }
  },
);

// Refresh Token
export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error("No refresh token");

      const response = await api.refreshToken(refreshToken);
      if (response.success === true) {
        setAuthData(response.data);
        return response.data.user || true;
      }
      return rejectWithValue(response.message || "Failed to refresh token");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to refresh token",
      );
    }
  },
);

// Logout
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await api.logout();
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  },
);

// ============= SLICE =============

const initialState = {
  user: getUserData(),
  isLoggedIn: isAuthenticated(),
  loading: false,
  error: null,
  otpData: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.isLoggedIn = false;
      state.loading = false;
      state.error = null;
      state.otpData = null;
      clearAuthData();
    },
    setOtpData: (state, action) => {
      state.otpData = action.payload;
    },
    // Keep the auth identity in Redux when the session is restored/refreshed.
    // Navbar and calculator both observe this slice, so no page refresh is needed.
    syncAuthenticatedUser: (state, action) => {
      if (action.payload) {
        state.user = action.payload;
        state.isLoggedIn = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Send OTP
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.otpData = action.payload;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Login
      .addCase(loginWithOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload?.user ?? null;
        state.isLoggedIn = true;
        state.otpData = null;
      })
      .addCase(loginWithOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload?.user ?? null;
        state.isLoggedIn = true;
        state.otpData = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get User Info
      .addCase(getUserInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        // Ignore a late profile response if the user logged out meanwhile.
        if (state.isLoggedIn) state.user = action.payload;
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // A transient profile fetch failure does not invalidate an OTP login.
        // Only a confirmed session-expiry/logout should clear auth state.
      })

      // Refresh Token
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = true;
        if (action.payload && typeof action.payload === "object") {
          state.user = action.payload;
        }
      })
      .addCase(refreshToken.rejected, (state) => {
        // Only the API manager expires sessions on confirmed 401/403.
        // Network errors must not log out an otherwise valid session.
        state.loading = false;
      })

      // Profile changes should immediately update the name/avatar in the navbar.
      .addCase(getUserProfile.fulfilled, (state, action) => {
        if (state.isLoggedIn) state.user = action.payload;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (state.isLoggedIn) state.user = action.payload;
      })
      .addCase(updateBankDetails.fulfilled, (state, action) => {
        if (state.isLoggedIn) state.user = action.payload.userData;
      })

      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isLoggedIn = false;
        state.loading = false;
        state.error = null;
        state.otpData = null;
        clearAuthData();
      })
      .addCase(logoutUser.rejected, (state) => {
        state.user = null;
        state.isLoggedIn = false;
        state.loading = false;
        state.error = null;
        state.otpData = null;
        clearAuthData();
      });
  },
});

export const { clearAuthError, logout, setOtpData, syncAuthenticatedUser } = authSlice.actions;
export default authSlice.reducer;
